import { z } from 'zod';

/**
 * メールアドレス検証関数
 *
 * カンマ区切りのメールアドレスを含む文字列の検証用
 */
const validateEmailAddresses = (value: string | undefined, allowEmpty = false): boolean => {
  if (!value) return allowEmpty;

  // カンマ区切りのメールアドレスを検証
  const emails = value
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean);

  if (emails.length === 0) return allowEmpty;

  // 各メールアドレスの形式をチェック
  return emails.every((email) => z.email().safeParse(email).success);
};

/**
 * メール送信用の共通バリデーションスキーマ
 */
export const emailSendFormSchema = z.object({
  to: z
    .string()
    .min(1, { message: '宛先は必須です' })
    .refine((value) => validateEmailAddresses(value, false), {
      message: '有効なメールアドレスを入力してください（カンマ区切りで複数指定可能）',
    }),
  cc: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((value) => validateEmailAddresses(value, true), {
      message: '有効なメールアドレスを入力してください（カンマ区切りで複数指定可能）',
    }),
  fromName: z.string().optional().or(z.literal('')),
  replyTo: z
    .email({ message: '有効なメールアドレスを入力してください' })
    .min(1, { message: '返信先アドレスは必須です' }),
  subject: z.string().min(1, { message: '件名は必須です' }),
  body: z.string().min(1, { message: '本文は必須です' }),
});

/**
 * メール送信フォームデータの型定義
 */
export type EmailSendFormData = z.infer<typeof emailSendFormSchema>;

export const emailSchema = z
  .string()
  .min(1, '必須項目です')
  .pipe(z.email('メールアドレスの形式が正しくありません'));
export const usernameSchema = z.string().min(1, '必須項目です');
/**
 * MFA のコード(認証アプリの6桁またはバックアップコード)
 * 空白・ハイフンの除去や大文字小文字の正規化は API 側で行うため、ここでは必須と長さだけを見る
 */
export const mfaCodeSchema = z.string().trim().min(1, '必須項目です').max(32, 'コードが長すぎます');
export const passwordSchema = z
  .string()
  .min(1, '必須項目です')
  .min(9, '9文字以上で入力してください')
  .regex(/^(?!\d+$).+$/, '数字のみのパスワードは使用できません')
  .regex(/^(?!.*(password|123456789|qwerty|admin)).*$/i, '一般的なパスワードは使用できません');

/**
 * パスワード確認フィールドスキーマ
 * 基本バリデーションのみ（一致チェックは別途refineで行う）
 */
export const passwordConfirmSchema = z.string().min(1, '必須項目です');

/**
 * パスワード + パスワード確認の共通スキーマ
 * 新規登録やパスワード変更で使用
 */
export const passwordWithConfirmSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: passwordConfirmSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'パスワードが一致しません',
    path: ['confirmPassword'],
  });

/**
 * 設定画面でのパスワード変更用スキーマ（現在 + 新しい + 確認）
 */
export const settingsPasswordChangeSchema = z
  .object({
    currentPassword: passwordSchema,
    newPassword: passwordSchema,
    confirmPassword: passwordConfirmSchema,
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: '新しいパスワードと確認用パスワードが一致しません',
    path: ['confirmPassword'],
  });
